import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from 'components';
import { type UserLoop } from 'types';
import OGUserLoopSelectorContainer from 'pages/AIMusicGenerator/components/FormulaItem/components/UserLoopSelector';
import { remixerApi } from 'services/api/remixer';
import { useMessageSnackbar } from 'services/hooks';
import { ExtraFormulaFields } from 'services/store/remixer/types';
import { events } from 'services/analytics';
import useAnalytics from 'services/hooks/analytics/useAnalytics';
import {
  StyledCTAContainer,
  StyledCancelButton,
  StyledDialogContainer,
  StyledDivider,
  StyledHeaderContainer,
  StyledTitle,
} from './styled';

interface UserLoopSelectorContainerProps {
  isOpen: boolean;
  onClose: () => void;
  data: UserLoop;
}

const UserLoopSelectorContainer = ({
  onClose,
  isOpen,
  data,
}: UserLoopSelectorContainerProps) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const [remixSong, { isLoading, isError }] = remixerApi.useRemixSongMutation();

  const { displayErrorSnackbar } = useMessageSnackbar();

  const [currentUserLoop, setCurrentUserLoop] = useState<UserLoop>();

  useEffect(() => {
    setCurrentUserLoop(data);
  }, [data]);

  const handleCtaClick = () => {
    track(events.studioRemixApplied(ExtraFormulaFields.UserLoop));
    remixSong({
      field: ExtraFormulaFields.UserLoop,
      value: currentUserLoop || data,
    });
    onClose();
  };

  useEffect(() => {
    if (isError) {
      displayErrorSnackbar(t('remixer:userLoop:selectorDialog:unknownError'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, t]);

  const handleCancelClick = () => {
    onClose();
  };

  return (
    <StyledDialogContainer onClose={onClose} open={isOpen} keepMounted>
      <StyledHeaderContainer>
        <StyledTitle> {t('remixer:userLoop:selectorDialog:title')}</StyledTitle>
      </StyledHeaderContainer>
      <OGUserLoopSelectorContainer
        value={currentUserLoop || data}
        onChange={setCurrentUserLoop}
      />

      <StyledDivider />

      <StyledCTAContainer>
        <StyledCancelButton onClick={handleCancelClick}>
          {t('remixer:userLoop:selectorDialog:cancel')}
        </StyledCancelButton>

        <LoadingButton
          variant="contained"
          onClick={handleCtaClick}
          loading={isLoading}
        >
          {t('remixer:userLoop:selectorDialog:cta')}
        </LoadingButton>
      </StyledCTAContainer>
    </StyledDialogContainer>
  );
};

export default UserLoopSelectorContainer;
